'use client'; // for server side rendering

import { type ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { type ThemeOptions, ThemeProvider } from '@mui/material/styles';

import { getTheme } from '../theme/theme';
import { ErrorBoundary } from './ErrorBoundary';

export interface VarnishAppProps {
    theme?: ThemeOptions;
    defaultMode?: 'light' | 'dark' | 'system';
    children: ReactNode | ReactNode[];
}

/**
 * A top level container for your application that handles errors cleanly,
 * makes the required theme available to child components, and sets up
 * the application layout.
 *
 * If the theme property is provided, it's deeply merged with the default Varnish
 * theme. Which means you can only provide the properties you'd like to override,
 * and those not provided will use the defaults.
 */
export function VarnishApp({ theme, defaultMode, children }: VarnishAppProps) {
    const mergedTheme = getTheme(theme);
    return (
        <ThemeProvider theme={mergedTheme} defaultMode={defaultMode || 'light'}>
            <CssBaseline />
            <ErrorBoundary>{children}</ErrorBoundary>
        </ThemeProvider>
    );
}

export default VarnishApp;
